import React, { createContext, useContext, useId, useMemo } from 'react';
import clsx from 'clsx';

/**
 * Tabs — accessible tabbed navigation.
 *
 * <Tabs value={tab} onChange={setTab}>
 *   <Tabs.List>
 *     <Tabs.Trigger value="updates">עדכונים</Tabs.Trigger>
 *     <Tabs.Trigger value="businesses">עסקים</Tabs.Trigger>
 *   </Tabs.List>
 *   <Tabs.Panel value="updates"> ... </Tabs.Panel>
 *   <Tabs.Panel value="businesses"> ... </Tabs.Panel>
 * </Tabs>
 *
 * Variant: pill (default — full pill background per active tab), underline (line under active tab)
 */
const TabsContext = createContext(null);

export function Tabs({ value, onChange, variant = 'pill', className, children, fullWidth = false }) {
    const baseId = useId();
    const ctx = useMemo(() => ({ value, onChange, variant, baseId, fullWidth }), [value, onChange, variant, baseId, fullWidth]);
    return (
        <TabsContext.Provider value={ctx}>
            <div className={className}>{children}</div>
        </TabsContext.Provider>
    );
}

function List({ children, className }) {
    const { variant, fullWidth } = useContext(TabsContext);
    return (
        <div
            role="tablist"
            className={clsx(
                'inline-flex items-center',
                variant === 'pill' && 'p-1 gap-1 bg-surface-muted rounded-pill border border-line-subtle',
                variant === 'underline' && 'gap-1 border-b border-line-subtle w-full',
                fullWidth && 'w-full',
                className,
            )}
        >
            {children}
        </div>
    );
}

function Trigger({ value, children, icon, disabled = false, className }) {
    const ctx = useContext(TabsContext);
    const active = ctx.value === value;

    const pillActive =
        'bg-surface text-ink shadow-sm ring-1 ring-line-subtle';
    const pillIdle =
        'text-ink-muted hover:text-ink hover:bg-surface/60';
    const underlineActive =
        'text-brand-700 border-brand-500';
    const underlineIdle =
        'text-ink-muted hover:text-ink border-transparent';

    return (
        <button
            type="button"
            role="tab"
            id={`${ctx.baseId}-trigger-${value}`}
            aria-controls={`${ctx.baseId}-panel-${value}`}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            onClick={() => !disabled && ctx.onChange?.(value)}
            className={clsx(
                'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap select-none',
                'transition-[background-color,color,box-shadow,border-color] duration-200 ease-smooth',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                ctx.fullWidth && 'flex-1',
                ctx.variant === 'pill' && [
                    'px-4 py-2 text-sm rounded-pill',
                    active ? pillActive : pillIdle,
                ],
                ctx.variant === 'underline' && [
                    'px-3 py-2.5 text-sm border-b-2 -mb-px',
                    active ? underlineActive : underlineIdle,
                ],
                className,
            )}
        >
            {icon && <span className="inline-flex items-center w-4 h-4" aria-hidden="true">{icon}</span>}
            <span>{children}</span>
        </button>
    );
}

function Panel({ value, children, className }) {
    const ctx = useContext(TabsContext);
    if (ctx.value !== value) return null;
    return (
        <div
            role="tabpanel"
            id={`${ctx.baseId}-panel-${value}`}
            aria-labelledby={`${ctx.baseId}-trigger-${value}`}
            className={clsx('animate-fade-in', className)}
        >
            {children}
        </div>
    );
}

Tabs.List = List;
Tabs.Trigger = Trigger;
Tabs.Panel = Panel;

export default Tabs;
